
import React from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Heart, MessageCircle, PiggyBank, Users, Gift } from "lucide-react";
import { Button } from "@/components/ui/button";

interface StepProps {
  number: number;
  icon: React.ReactNode;
  title: string;
  description: string;
  example: string;
}

const Step = ({ number, icon, title, description, example }: StepProps) => {
  return (
    <div className="relative flex flex-col md:flex-row items-center md:items-start gap-4 md:gap-6 h-full group">
      {/* Step number and icon */}
      <div className="flex-shrink-0 order-1 md:order-1">
        <div className="relative">
          <div className="bg-legatia-orange/20 rounded-full w-16 h-16 md:w-20 md:h-20 flex items-center justify-center group-hover:bg-legatia-orange/30 transition-colors">
            {icon}
          </div>
          <div className="absolute -top-2 -right-2 bg-legatia-orange text-white w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm">
            {number}
          </div>
        </div>
      </div>
      
      {/* Content */}
      <div className="flex-1 text-center md:text-left order-2 md:order-2">
        <h3 className="text-lg md:text-xl font-bold mb-2 md:mb-3 min-h-[2.5rem] flex items-center justify-center md:justify-start text-gray-900">
          {title}
        </h3>
        <p className="text-gray-600 text-sm md:text-base leading-relaxed mb-2">
          {description}
        </p>
        <p className="text-legatia-orange text-sm font-medium italic">
          {example}
        </p>
      </div>
    </div>
  );
};

const HowItWorksSection = () => {
  const { language } = useLanguage();
  
  const content = {
    fr: {
      title: "Comment créer leur cagnotte cadeau ?",
      subtitle: "En 5 étapes simples, créez un cadeau qui grandit avec eux",
      cta: "Créer leur cagnotte cadeau",
      steps: [
        {
          title: "Créez leur cagnotte",
          description: "Ouvrez un compte cadeau sécurisé pour l'occasion qui vous tient à cœur.",
          example: "Exemple : Pour ses 18 ans ou son diplôme"
        },
        {
          title: "Écrivez votre message",
          description: "Laissez-leur un mot d'amour, des conseils, ou vos souhaits les plus chers.",
          example: "Exemple : Une lettre vidéo de grand-maman"
        },
        {
          title: "Épargnez à votre rythme",
          description: "Versez le montant qui vous convient, quand vous le souhaitez.",
          example: "Exemple : 50€ par mois qui deviennent 15 000€"
        },
        {
          title: "Invitez vos proches",
          description: "Famille et amis peuvent rejoindre cette belle aventure collective.",
          example: "Exemple : Grand-papa, tonton et marraine participent"
        },
        {
          title: "Le jour magique arrive",
          description: "Ils découvrent vos messages d'amour et reçoivent leur trésor.",
          example: "Exemple : Émotion garantie le jour de ses 18 ans !"
        }
      ]
    },
    en: {
      title: "How to create their gift fund?",
      subtitle: "In 5 simple steps, create a gift that grows with them",
      cta: "Create their gift fund",
      steps: [
        {
          title: "Create their fund",
          description: "Open a secure gift account for the occasion close to your heart.",
          example: "Example: For their 18th birthday or graduation"
        },
        {
          title: "Write your message",
          description: "Leave them a word of love, advice, or your dearest wishes.",
          example: "Example: A video letter from grandma"
        },
        {
          title: "Save at your pace",
          description: "Contribute the amount that suits you, when you want.",
          example: "Example: €50 per month that becomes €15,000"
        },
        {
          title: "Invite your loved ones",
          description: "Family and friends can join this beautiful collective adventure.",
          example: "Example: Grandpa, uncle and godmother participate"
        },
        {
          title: "The magical day arrives",
          description: "They discover your messages of love and receive their treasure.",
          example: "Example: Guaranteed emotion on their 18th birthday!"
        }
      ]
    },
    de: {
      title: "Wie erstelle ich ihren Geschenkfonds?",
      subtitle: "In 5 einfachen Schritten ein Geschenk schaffen, das mit ihnen wächst",
      cta: "Geschenkfonds erstellen",
      steps: [
        {
          title: "Erstellen Sie ihren Fonds",
          description: "Eröffnen Sie ein sicheres Geschenkkonto für den Anlass, der Ihnen am Herzen liegt.",
          example: "Beispiel: Für ihren 18. Geburtstag oder Abschluss"
        },
        {
          title: "Schreiben Sie Ihre Nachricht",
          description: "Hinterlassen Sie ihnen ein Wort der Liebe, Ratschläge oder Ihre liebsten Wünsche.",
          example: "Beispiel: Ein Videobrief von Oma"
        },
        {
          title: "Sparen Sie in Ihrem Tempo",
          description: "Zahlen Sie den Betrag ein, der Ihnen passt, wann Sie möchten.",
          example: "Beispiel: 50€ pro Monat werden zu 15.000€"
        },
        {
          title: "Laden Sie Ihre Lieben ein",
          description: "Familie und Freunde können sich diesem schönen kollektiven Abenteuer anschließen.",
          example: "Beispiel: Opa, Onkel und Patin nehmen teil"
        },
        {
          title: "Der magische Tag kommt",
          description: "Sie entdecken Ihre Liebesbotschaften und erhalten ihren Schatz.",
          example: "Beispiel: Garantierte Emotion an ihrem 18. Geburtstag!"
        }
      ]
    }
  };
  
  const textContent = content[language];
  
  const icons = [
    <PiggyBank className="h-6 w-6 md:h-8 md:w-8 text-legatia-orange" />,
    <MessageCircle className="h-6 w-6 md:h-8 md:w-8 text-legatia-orange" />,
    <Heart className="h-6 w-6 md:h-8 md:w-8 text-legatia-orange" />,
    <Users className="h-6 w-6 md:h-8 md:w-8 text-legatia-orange" />,
    <Gift className="h-6 w-6 md:h-8 md:w-8 text-legatia-orange" />
  ];

  return (
    <section id="how-it-works" className="py-16 md:py-20 px-4 bg-white">
      <div className="container mx-auto max-w-5xl">
        <div className="text-center mb-12 md:mb-16">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4 text-gray-900">
            {textContent.title}
          </h2>
          <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
            {textContent.subtitle}
          </p>
        </div>
        
        <div className="space-y-8 md:space-y-12 mb-12">
          {textContent.steps.map((step, index) => (
            <div key={index} className="relative">
              {/* Connection line between steps - only on desktop */}
              {index < textContent.steps.length - 1 && (
                <div className="absolute left-8 md:left-10 top-16 md:top-20 bottom-0 w-0.5 bg-legatia-orange/20 hidden md:block"></div>
              )}
              
              <Step 
                number={index + 1}
                icon={icons[index]}
                title={step.title}
                description={step.description}
                example={step.example}
              />
            </div>
          ))}
        </div>

        {/* Trust and reassurance section */}
        <div className="bg-gradient-to-r from-legatia-light to-legatia-yellow/10 rounded-2xl p-6 md:p-8 mb-8">
          <div className="text-center mb-6">
            <h3 className="text-lg md:text-xl font-bold mb-2 text-gray-900">
              {language === 'fr' && "Pourquoi les familles nous font confiance ?"}
              {language === 'en' && "Why do families trust us?"}
              {language === 'de' && "Warum vertrauen uns Familien?"}
            </h3>
          </div>
          
          <div className="grid md:grid-cols-3 gap-4 md:gap-6">
            <div className="text-center">
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <h4 className="font-semibold text-gray-900 mb-2">
                  {language === 'fr' && "Sécurité bancaire"}
                  {language === 'en' && "Banking security"}
                  {language === 'de' && "Banksicherheit"}
                </h4>
                <p className="text-sm text-gray-600">
                  {language === 'fr' && "Standards suisses, protection totale"}
                  {language === 'en' && "Swiss standards, total protection"}
                  {language === 'de' && "Schweizer Standards, totaler Schutz"}
                </p>
              </div>
            </div>
            
            <div className="text-center">
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <h4 className="font-semibold text-gray-900 mb-2">
                  {language === 'fr' && "Vous gardez le contrôle"}
                  {language === 'en' && "You stay in control"}
                  {language === 'de' && "Sie behalten die Kontrolle"}
                </h4>
                <p className="text-sm text-gray-600">
                  {language === 'fr' && "Modifiez tout à tout moment"}
                  {language === 'en' && "Change everything anytime"}
                  {language === 'de' && "Ändern Sie alles jederzeit"}
                </p>
              </div>
            </div>
            
            <div className="text-center">
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <h4 className="font-semibold text-gray-900 mb-2">
                  {language === 'fr' && "Zéro frais cachés"}
                  {language === 'en' && "Zero hidden fees"}
                  {language === 'de' && "Null versteckte Gebühren"}
                </h4>
                <p className="text-sm text-gray-600">
                  {language === 'fr' && "Transparence totale garantie"}
                  {language === 'en' && "Total transparency guaranteed"}
                  {language === 'de' && "Totale Transparenz garantiert"}
                </p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="text-center">
          <Button 
            className="bg-gradient-to-r from-legatia-orange to-legatia-red hover:from-legatia-orange/90 hover:to-legatia-red/90 text-white text-lg px-8 py-4 rounded-full transition-all transform hover:shadow-lg hover:-translate-y-1 flex items-center gap-2 mx-auto"
            onClick={() => window.location.href = "https://app.getlegatia.ch/"}
          >
            <Heart className="h-5 w-5" />
            {textContent.cta}
          </Button>
        </div>
      </div>
    </section>
  );
};

export default HowItWorksSection;
